import { useSearch } from '@tanstack/react-location'
import { useFormik } from 'formik'
import ActionSuccessModal from 'MediaManager/components/ActionSuccessModal'
import Input from 'MediaManager/components/Form/Input'
import ScreenLayout from 'MediaManager/layouts/ScreenLayout'
import { getVendors } from 'MediaManager/services'
import { getDesignLayoutsConfigurationOptionsFields } from 'MediaManager/services/designs'
import { getUnitType } from 'MediaManager/services/property'
import { deleteRoomDesign, upsertRoomDesign, getRoomDesignClassifications } from 'MediaManager/services/roomDesigns'
import { useCallback, useEffect, useMemo, useState } from 'react'
import Button from 'shared/components/Button'
import Loader from 'shared/components/Loader'
import Select from 'shared/components/Select'
import { useRequest } from 'shared/hooks/useRequest'
import { errorMessages } from 'shared/settings'
import * as Yup from 'yup'

function RoomDesignsEditScreen(props) {
  const { line, id } = useSearch()

  const [resultValue, setResultValue] = useState()

  const getConfigurationOptionsFields = useCallback(async () => {
    const fields = await getDesignLayoutsConfigurationOptionsFields()
    return fields.filter((f) => !['id', 'design_layout'].includes(f.name))
  }, [line])

  const { data: configurationFields } = useRequest(getConfigurationOptionsFields)

  const getVendorsData = useCallback(async () => {
    const { data } = await getVendors()
    return data.vendors
  }, [line])

  const { data: vendors } = useRequest(getVendorsData)

  const getRoomDesignClassificationsCb = useCallback(async () => {
    const { data } = await getRoomDesignClassifications()
    return data.room_design_classifications
  }, [line])

  const { data: roomDesignClassifications } = useRequest(getRoomDesignClassificationsCb)

  const getData = useCallback(async () => {
    const { data } = await getUnitType(line)
    return data.unit_types[0]
  }, [line])

  const { data, loading, refresh } = useRequest(getData)

  const roomDesign = useMemo(() => {
    if (data) {
      const roomDesigns = data.unit_type_levels
        .map((l) =>
          l.rooms.map((r) => r.room_designs.map((d) => ({ ...d, unit_type_level: l, room_id: r.id })).flat()).flat(),
        )
        .flat()
      return roomDesigns.find((i) => i.id === Number(id))
    }
  }, [data, id])

  const roomDesignClassificationsOptions = useMemo(() => {
    if (roomDesignClassifications) {
      return roomDesignClassifications.map((d) => ({
        label: d.name,
        value: d.name,
      }))
    }
    return []
  }, [roomDesignClassifications])

  const vendorOptions = useMemo(() => {
    if (vendors) {
      return vendors.map((v) => ({
        label: v.full_name,
        value: v.id,
      }))
    }
    return []
  }, [vendors])

  useEffect(() => {
    if (data) {
      formik.setValues({
        level: roomDesign?.unit_type_level?.id,
        roomDesign: roomDesign?.roomDesign,
        main_vendor_id: roomDesign?.main_vendor?.id,
        design_name: roomDesign?.design_name,
        short_description: roomDesign?.short_description,
        design_classification: roomDesign?.design_classification,
        room_id: roomDesign?.room_id,
      })
    }
  }, [data, roomDesign])

  const validationSchema = Yup.object().shape({
    room_id: Yup.number().required(errorMessages.required),
    main_vendor_id: Yup.number().required(errorMessages.required),
    level: Yup.number().required(errorMessages.required),
    design_classification: Yup.string().required(errorMessages.required),
  })

  const formik = useFormik({
    initialValues: {},
    validateOnBlur: true,
    validationSchema,
    initialTouched: false,
    validateOnMount: true,
    onSubmit: async () => {
      try {
        const { main_vendor_id, room_id, design_name, short_description, design_classification } = formik.values
        const { data } = await upsertRoomDesign({
          ...(id ? { id } : {}),
          main_vendor_id,
          design_name,
          short_description,
          design_classification,
          room_id,
        })

        setResultValue('success')
      } catch (error) {
        setResultValue('error')
      }
    },
  })

  const rooms = useMemo(() => {
    if (data) {
      return data.unit_type_levels.map((l) => l.rooms).flat()
    }
    return []
  }, [data])

  const roomOptions = useMemo(() => {
    if (data) {
      return rooms.map((r) => ({
        label: r.room_classification.classification_name,
        value: r.id,
      }))
    }
    return []
  }, [data, rooms])

  useEffect(() => {
    if (formik.values.room_id && rooms && data) {
      formik.setFieldValue('level', rooms.find((r) => r.id === formik.values.room_id)?.unit_type_level?.id)
    }
  }, [formik.values.room_id, data])

  const isValid = useMemo(() => {
    return Object.values(formik.errors).every((v) => !v)
  }, [formik, data])

  function getFieldError(field) {
    return formik.errors[field] && formik.touched[field] ? formik.errors[field] : null
  }

  async function onDelete() {
    try {
      await deleteRoomDesign(id)
      setResultValue('success')
    } catch (error) {
      setResultValue('error')
    }
  }

  const levelText = useMemo(() => {
    if (data && formik.values.level) {
      return data.unit_type_levels.find((l) => l.id === formik.values.level)?.floor_number
    }
  }, [formik.values.level, data])

  return (
    <ScreenLayout title={`${id ? 'Update' : 'Add'} room design`}>
      {resultValue && (
        <ActionSuccessModal
          action={`Room design`}
          successText="has been successfully upserted."
          errorText="upserting failed. Please try again."
          state={resultValue}
          error={'Please try again later.'}
          skipConfirm
          skipBack
          onClose={() => {
            if (resultValue === 'success') {
              window.history.back()
            } else {
              setResultValue(null)
            }
          }}
        />
      )}
      {loading && (
        <div>
          <Loader />
        </div>
      )}
      {!loading && (
        <>
          <div>
            <Input label="Property" disabled value={data.property.property_name} />
            <Input wrapClassName="mt-[16px]" label="Line" disabled value={data.name} />
          </div>
          {roomDesign && (
            <div className="mt-[16px] opacity-50">
              <Input
                label="Design layout ID"
                name="design_layout_id"
                onChange={formik.handleChange}
                value={roomDesign.design_layout.id}
              />
            </div>
          )}
          <div className="mt-[16px]">
            <form onSubmit={formik.handleSubmit}>
              <div className="grid grid-cols-2 gap-[16px]">
                <div className="col-span-2">
                  <Select
                    options={roomOptions}
                    required
                    error={getFieldError('room')}
                    value={roomOptions.find((v) => v.value === formik.values.room_id)}
                    onChange={(val) => formik.setFieldValue('room_id', val.value)}
                    label="Room"
                  />
                </div>
                <div className="col-span-2">
                  <Input
                    label="Level"
                    disabled
                    name="level"
                    onChange={formik.handleChange}
                    value={levelText}
                    placeholder="Please select room first"
                  />
                </div>
                <div className="col-span-2">
                  <Select
                    options={vendorOptions}
                    required
                    error={getFieldError('vendor')}
                    value={vendorOptions.find((v) => v.value === formik.values.main_vendor_id)}
                    onChange={(val) => formik.setFieldValue('main_vendor_id', val.value)}
                    label="Main vendor"
                  />
                </div>

                <div className="col-span-2">
                  <Select
                    options={roomDesignClassificationsOptions}
                    required
                    error={getFieldError('design_classification')}
                    value={roomDesignClassificationsOptions.find(
                      (v) => v.value === formik.values.design_classification,
                    )}
                    onChange={(val) => formik.setFieldValue('design_classification', val.value)}
                    label="Design Clasisification"
                  />
                </div>
                <div className="col-span-2">
                  <Input
                    label="Design name"
                    name="design_name"
                    onChange={formik.handleChange}
                    value={formik.values.design_name}
                  />
                </div>
                <div className="col-span-2">
                  <Input
                    label="Short description"
                    name="short_description"
                    onChange={formik.handleChange}
                    value={formik.values.short_description}
                  />
                </div>
              </div>

              <div
                className={`flex justify-end items-center mt-[16px] ${
                  formik.isSubmitting ? 'pointer-events-none  opacity-50' : ''
                }`}
              >
                {id && (
                  <Button rounded variant="secondary" onClick={onDelete} className="mr-[16px]">
                    Delete
                  </Button>
                )}
                <Button rounded variant="secondary" onClick={() => window.history.back()}>
                  Cancel
                </Button>
                <Button loading={formik.isSubmitting} disabled={!isValid} rounded className="ml-[16px]" type="submit">
                  {id ? 'Update' : 'Add'}
                </Button>
              </div>
            </form>
          </div>
        </>
      )}
    </ScreenLayout>
  )
}

export default RoomDesignsEditScreen
